// 261015 P4c-1 (D11) — sơ đồ quan hệ SVG tự vẽ (KHÔNG thư viện). Bố cục + lọc ở `relationGraphLayout.ts` (thuần, có jest); file này chỉ vẽ.
// Truy cập: mỗi người là nút bấm được bằng bàn phím; luôn kèm DANH SÁCH quan hệ dạng câu (đọc được khi sơ đồ đông).
import React, { useMemo, useState } from 'react';
import { Button, Input, Select } from 'antd';
import {
  FILTER_LABEL, layoutRelationGraph, RELATION_LABEL, type GraphInput, type RelationFilter,
} from './relationGraphLayout';
import { defaultFocusId, type ClaimView } from './overviewView';
import type { OverviewRelationshipType } from '../../../../types/adaptation';
import { Pill } from '../WbSlot';

export interface RelationListRow {
  id: string; sentence: string; factionLabel: string; type: OverviewRelationshipType; aId: string; bId: string;
  /** Ý nền (chỉ tổng quan có trích). Thiếu ⇒ dòng "chưa xác minh trích". */
  claims?: ClaimView[]; verified?: boolean;
}

interface Props {
  input: GraphInput;
  rows: RelationListRow[];
  /** Có ⇒ sơ đồ KHÔNG có trích (quan hệ ③ cũ): dải ghi chú + nhãn "chưa xác minh trích". */
  unverifiedNote?: string | null;
  canEdit?: boolean;
  onEvidence?: (claimId: string) => void;
  onEditRelation?: (id: string) => void;
  onAddRelation?: () => void;
  onAsk?: (id: string, sentence: string) => void;
}

const FILTERS: RelationFilter[] = ['all', 'family', 'power', 'ally-rival'];

const RelationGraph: React.FC<Props> = ({ input, rows, unverifiedNote, canEdit, onEvidence, onEditRelation, onAddRelation, onAsk }) => {
  const initial = useMemo(() => defaultFocusId(input) ?? '', [input]);
  const [focusRaw, setFocus] = useState<string | null>(null);
  const [filter, setFilter] = useState<RelationFilter>('all');
  const [query, setQuery] = useState('');
  const focus = focusRaw === null ? initial : focusRaw;
  const focusKnown = !!focus && input.nodes.some((n) => n.id === focus);
  const layout = useMemo(() => layoutRelationGraph(input, { focusId: focusKnown ? focus : null, filter, query }), [input, focus, focusKnown, filter, query]);
  const nodeLabel = (id: string): string => input.nodes.find((n) => n.id === id)?.label ?? id;

  const edgeIds = new Set(layout.edges.map((e) => e.id));
  const shownRows = rows.filter((r) => edgeIds.has(r.id));   // đúng những quan hệ đang vẽ (đã qua chọn người + lọc loại)
  const options = [{ value: '', label: `Xem toàn bộ ${input.nodes.length} người` }, ...[...input.nodes].sort((a, b) => a.rank - b.rank || a.label.localeCompare(b.label, 'vi')).map((n) => ({ value: n.id, label: n.label }))];

  const description = `${layout.mode === 'focus' ? `Quan hệ của ${nodeLabel(focus)}` : 'Toàn cảnh quan hệ'}: ${layout.shownEdges} quan hệ giữa ${layout.shownNodes} người. Danh sách đầy đủ bằng chữ ở bên dưới.`;

  return (
    <div className="ov-graph">
      <div className="ov-graph-tools">
        <Select
          showSearch optionFilterProp="label" style={{ minWidth: 230 }} value={focusKnown ? focus : ''} options={options} aria-label="Tập trung nhân vật"
          onChange={(v) => setFocus(v || '')}
        />
        <Select style={{ minWidth: 190 }} value={filter} options={FILTERS.map((f) => ({ value: f, label: FILTER_LABEL[f] }))} aria-label="Loại quan hệ" onChange={(v) => setFilter(v as RelationFilter)} />
        <Input allowClear style={{ width: 200 }} placeholder="Tìm tên trong sơ đồ…" value={query} onChange={(e) => setQuery(e.target.value)} aria-label="Tìm tên trong sơ đồ" />
        {canEdit && onAddRelation && <Button size="small" onClick={onAddRelation}>Thêm quan hệ</Button>}
      </div>
      {unverifiedNote && <div className="ov-note ov-note-warn" role="note">{unverifiedNote}</div>}
      <div className="ov-graph-scroll">
        <svg viewBox={`0 0 ${layout.width} ${layout.height}`} width={layout.width} height={layout.height} role="img" aria-label={description}>
          <title>{description}</title>
          <defs>
            <marker id="ov-arrow" viewBox="0 0 10 10" refX="9" refY="5" markerWidth="7" markerHeight="7" orient="auto-start-reverse"><path d="M0 0 L10 5 L0 10z" className="ov-arrow" /></marker>
          </defs>
          {layout.groups.map((g) => (
            <g key={`g-${g.id}`} className="ov-grp">
              <rect x={g.x} y={g.y} width={g.w} height={g.h} rx={12} className="ov-grp-box" />
              <text x={g.x + 12} y={g.y + 17} className="ov-grp-label">{g.label}</text>
            </g>
          ))}
          {layout.edges.map((e) => (
            <g key={e.id} className="ov-edge">
              <line x1={e.x1} y1={e.y1} x2={e.x2} y2={e.y2} className={`ov-edge-line${e.directed ? ' ov-edge-dir' : ''}`} markerEnd={e.directed ? 'url(#ov-arrow)' : undefined} />
              <title>{`${nodeLabel(e.a)} — ${nodeLabel(e.b)}: ${RELATION_LABEL[e.type]}`}</title>
              {e.labelText && <text x={e.labelX} y={e.labelY} textAnchor="middle" className="ov-edge-label">{e.labelText}</text>}
            </g>
          ))}
          {layout.nodes.map((n) => (
            <g
              key={n.id} tabIndex={0} role="button" aria-label={`${n.fullLabel}${n.focus ? ' (đang chọn)' : ''} — bấm để xem quan hệ`}
              className={`ov-node${n.focus ? ' ov-node-focus' : ''}${n.matched ? ' ov-node-match' : ''}${n.dim ? ' ov-node-dim' : ''}${n.cut ? ' ov-node-cut' : ''}`}
              onClick={() => setFocus(n.focus ? '' : n.id)}
              onKeyDown={(ev) => { if (ev.key === 'Enter' || ev.key === ' ') { ev.preventDefault(); setFocus(n.focus ? '' : n.id); } }}
            >
              <rect x={n.x} y={n.y} width={n.w} height={n.h} rx={8} />
              <text x={n.cx} y={n.cy + 4} textAnchor="middle">{n.label}</text>
              <title>{`${n.fullLabel}${n.cut ? ' (đã cắt / gộp khỏi phim)' : ''}`}</title>
            </g>
          ))}
        </svg>
      </div>
      <div className="ov-legend">
        Mũi tên: cha/mẹ → con, chủ → tớ. Đường thường: quan hệ hai chiều (anh chị em, vợ chồng, họ hàng, cùng phe, đồng minh, đối thủ). Ô mờ gạch: nhân vật đã cắt / gộp khỏi phim.
        {' '}Đang hiện {layout.shownNodes}/{layout.totalNodes} người, {layout.shownEdges}/{layout.totalEdges} quan hệ.
        {layout.mode === 'focus' && <Button type="link" size="small" onClick={() => setFocus('')}>Xem toàn bộ</Button>}
      </div>
      <details open={rows.length <= 20}>
        <summary>Danh sách quan hệ ({shownRows.length}) — đọc bằng bàn phím / khi sơ đồ quá đông</summary>
        {shownRows.length === 0 ? <div className="ov-empty">Chưa có quan hệ nào{filter !== 'all' ? ' thuộc loại này' : ''}.</div> : (
          <ul className="ov-rel-list">
            {shownRows.map((r) => (
              <li key={r.id}>
                <span>{r.sentence}</span>
                {r.factionLabel && <Pill tone="ind">{r.factionLabel}</Pill>}
                {r.claims ? (r.verified ? <Pill tone="ok">đã tìm thấy trích</Pill> : <Pill tone="bad">chưa xác minh</Pill>) : <Pill tone="todo">chưa xác minh trích</Pill>}
                {(r.claims ?? []).map((c) => <button key={c.id} type="button" className="ov-linkbtn" onClick={() => onEvidence?.(c.id)}>trích {c.id}</button>)}
                {canEdit && onEditRelation && r.claims && <button type="button" className="ov-linkbtn" onClick={() => onEditRelation(r.id)}>Sửa</button>}
                {onAsk && r.claims && <button type="button" className="ov-linkbtn" onClick={() => onAsk(r.id, r.sentence)}>Nhờ trợ lý</button>}
              </li>
            ))}
          </ul>
        )}
      </details>
    </div>
  );
};

export default RelationGraph;
